import React, { useState } from "react";
import { Loader2, Send, CheckCircle2, Clock, Youtube } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ImageUpload from "@/components/tournament/ImageUpload";
import { GENRES, PLATFORMS, Chip } from "@/components/games/gameMeta";

const inp = "w-full px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[hsl(var(--primary))]";
const EMPTY = { img: "", publisher: "", name: "", description: "", genres: [], platforms: [], screenshot: "", trailer_url: "" };
const lbl = "text-xs text-white/50 mb-1.5 block";

const toggle = (list, v, max) => (list.includes(v) ? list.filter((x) => x !== v) : max && list.length >= max ? list : [...list, v]);

export default function PublishGameModal({ open, onClose, onSubmitted }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const ready = f.img && f.name.trim() && f.publisher.trim() && f.description.trim().length >= 10 && f.genres.length && f.platforms.length;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/gamehub/submit", f);
      setDone(r.data); setF(EMPTY); onSubmitted?.(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? "Please check every field" : d || "Could not submit game");
    } finally { setBusy(false); }
  };
  const close = () => { setDone(null); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="bg-[#0d1117] border-white/10 text-white max-w-lg max-h-[92vh] overflow-y-auto" data-testid="publish-game-modal">
        {done ? (
          <div className="text-center py-4" data-testid="publish-game-success">
            <span className="mx-auto w-14 h-14 rounded-2xl bg-[hsl(var(--primary))]/15 grid place-items-center text-[hsl(var(--primary))]"><Clock size={26} /></span>
            <h3 className="mt-4 text-xl font-bold">Game submitted!</h3>
            <p className="mt-2 text-sm text-white/60"><b className="text-white">{done.name}</b> is waiting for admin approval. We'll notify you as soon as it's published.</p>
            <button onClick={close} data-testid="publish-game-done" className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-bold"><CheckCircle2 size={16} /> Got it</button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-head text-xl">Publish your game</DialogTitle>
              <DialogDescription className="text-white/50">Share your game with the community and let players rate it 1–10.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <span className={lbl}>Game picture (cover)</span>
                <ImageUpload value={f.img} onChange={(v) => setF({ ...f, img: v })} testid="publish-game-image" aspect="aspect-video" label="Upload game picture" endpoint="/gamehub/upload" maxMB={8} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input className={inp} value={f.name} onChange={set("name")} maxLength={80} placeholder="Game name" data-testid="publish-game-name" />
                <input className={inp} value={f.publisher} onChange={set("publisher")} maxLength={80} placeholder="Publisher" data-testid="publish-game-publisher" />
              </div>
              <div>
                <textarea className={`${inp} min-h-[88px] resize-none`} value={f.description} onChange={set("description")} maxLength={600} placeholder="Short description" data-testid="publish-game-description" />
                <div className="text-right text-[11px] text-white/30">{f.description.length}/600</div>
              </div>
              <div>
                <span className={lbl}>Genre (pick 1–3)</span>
                <div className="flex flex-wrap gap-1.5">{GENRES.map((g) => <Chip key={g} on={f.genres.includes(g)} onClick={() => setF({ ...f, genres: toggle(f.genres, g, 3) })} testid={`publish-genre-${g}`}>{g}</Chip>)}</div>
              </div>
              <div>
                <span className={lbl}>Platform</span>
                <div className="flex flex-wrap gap-1.5">{PLATFORMS.map((p) => <Chip key={p} on={f.platforms.includes(p)} onClick={() => setF({ ...f, platforms: toggle(f.platforms, p) })} testid={`publish-platform-${p}`}>{p}</Chip>)}</div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <span className={lbl}>Screenshot (optional)</span>
                  <ImageUpload value={f.screenshot} onChange={(v) => setF({ ...f, screenshot: v })} testid="publish-game-screenshot" aspect="aspect-video" label="Upload screenshot" endpoint="/gamehub/upload" maxMB={8} />
                </div>
                <div>
                  <span className={lbl}>YouTube trailer (optional)</span>
                  <div className="relative"><Youtube size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-red-500" />
                    <input className={`${inp} pl-9`} value={f.trailer_url} onChange={set("trailer_url")} maxLength={300} placeholder="https://youtu.be/…" data-testid="publish-game-trailer" /></div>
                </div>
              </div>
              <p className="text-[11px] text-white/40">Images are compressed automatically. Ratings come from the community only, and every game is reviewed by an admin before it goes public.</p>
              <button onClick={submit} disabled={busy || !ready} data-testid="publish-game-submit"
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-bold disabled:opacity-50">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Submit for review
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
